(ns app.openrouter.pricing
  "Pure customer-price estimates shared by the catalog, modal and credit
   check. These never determine the actual bill, which uses provider usage."
  (:require [clojure.string :as str]))

(defn number-value
  [value]
  (when (some? value)
    (let [n #?(:clj (try (Double/parseDouble (str value))
                         (catch NumberFormatException _ nil))
               :cljs (js/Number value))]
      (when (and n #?(:clj (Double/isFinite n) :cljs (js/isFinite n)))
        n))))

(defn default-duration
  "The offered duration closest to five seconds, the shorter one on a tie.
   The first listed duration made list prices incomparable (1s clips next
   to 8s ones) and generated 1-2s clips by default."
  [durations]
  (first (sort-by (fn [seconds]
                    [(abs (- seconds 5)) seconds])
                  durations)))

(defn default-resolution
  [resolutions]
  (or (some #{"720p"} resolutions)
      (first resolutions)))

(defn default-aspect-ratio
  [aspect-ratios]
  (or (some #{"9:16"} aspect-ratios)
      (first aspect-ratios)))

(defn video-options
  "Resolve missing options exactly like the modal's initial selection.
   Select values arrive as strings in the browser."
  [model options]
  {:duration (number-value (or (:duration options)
                              (default-duration (:model/durations model))))
   :resolution (or (:resolution options)
                   (default-resolution (:model/resolutions model)))
   :aspect-ratio (or (:aspect-ratio options)
                     (default-aspect-ratio (:model/aspect-ratios model)))})

(defn option-hints
  "Short generation settings for estimates and statement descriptions."
  [{:keys [duration resolution aspect-ratio characters]}]
  (str/join " " (cond-> []
                  duration (conj (str #?(:clj (long duration) :cljs duration) "s"))
                  resolution (conj resolution)
                  aspect-ratio (conj aspect-ratio)
                  characters (conj (str characters " chars")))))

(def resolution-pattern #"_(\d+p|\d+k)(?=_|$)")

(def nominal-sizes
  "The 16:9 size of each resolution. Its pixel count is the budget of the
   resolution for sizes that are listed by area instead of by short edge."
  {"480p" [854 480]
   "720p" [1280 720]
   "768p" [1366 768]
   "1080p" [1920 1080]
   "4k" [3840 2160]})

(defn video-pixels
  "Return the pixel count of the one advertised size for the resolution
   and aspect ratio: the size with the resolution's short edge, otherwise
   the size within the resolution's pixel budget (2/3 to 3/2 of its 16:9
   pixel count, which keeps 480p, 720p, 1080p and 4K apart). Seedance 2.5
   lists its 480p square as 640x640 and its 480p 21:9 as 992x432. No
   calculated sizes. The 2% ratio tolerance accommodates rounded
   dimensions."
  [{:keys [supported_sizes]} {:keys [resolution aspect-ratio]}]
  (let [[width short-edge] (get nominal-sizes (some-> resolution str/lower-case))
        [a b] (when aspect-ratio (map number-value (str/split aspect-ratio #":")))
        ratio (when (and a b (pos? a) (pos? b)) (/ a b))
        sizes (when (and short-edge ratio)
                (keep (fn [size]
                        (let [[w h] (map number-value (str/split size #"x"))]
                          (when (and w h (pos? h)
                                     (< (abs (- (/ w h) ratio)) (* 0.02 ratio)))
                            {:pixels (* w h)
                             :short-edge (min w h)})))
                      supported_sizes))
        unique-pixels (fn [matches]
                        (let [pixels (distinct (map :pixels matches))]
                          (when (= 1 (count pixels))
                            (first pixels))))
        exact (filter #(== short-edge (:short-edge %)) sizes)]
    (if (seq exact)
      (unique-pixels exact)
      (let [budget (when short-edge (* width short-edge))]
        (unique-pixels (filter #(<= (/ (* 2 budget) 3) (:pixels %) (/ (* 3 budget) 2))
                               sizes))))))

(defn video-sku-cost
  "Cost of a recognized text-to-video output SKU. Unknown dimensions or
   billing units produce no estimate rather than silently using 720p."
  [pricing options sku price]
  (let [seconds (:duration options)]
    (when (and seconds (pos? seconds))
      (cond
        (re-matches #"(?:text_to_video_)?duration_seconds(?:_with_audio|_without_audio)?" sku)
        (* price seconds)

        (re-matches #"cents_per_(?:second(?:_output)?|video_output_second)" sku)
        (/ (* price seconds) 100.0)

        (re-matches #"video_tokens(?:_without_audio)?" sku)
        (when-let [pixels (video-pixels pricing options)]
          (* price (/ (* pixels 24 seconds) 1024.0)))))))

(defn video-cost-estimate
  "Resolution-qualified SKUs replace the generic SKUs of their family
   (duration_seconds_480p over duration_seconds) and the generic silent or
   unspecified SKUs of their unit (at 4K, Seedance's video_tokens_4k over
   video_tokens_without_audio, whose lower-resolution rate would otherwise
   win). A generic with-audio rate stays unless its own family is
   qualified: where the provider exposes audio and silent rates but no
   selected audio mode, use the higher rate rather than promise a
   silent-only price for a potentially audible clip (Kling lists
   text_to_video_duration_seconds_720p next to duration_seconds_with_audio).
   Ignore image/input/continuation SKUs."
  [pricing options]
  (let [resolution (some-> (:resolution options) str/lower-case)
        candidates (keep (fn [[sku-key value]]
                           (let [sku (name sku-key)
                                 qualifier (some-> (re-find resolution-pattern sku)
                                                    second str/lower-case)
                                 family (str/replace sku resolution-pattern "")
                                 price (number-value value)]
                             (when (and price (not (neg? price))
                                        (or (nil? qualifier) (= qualifier resolution)))
                               (when-let [cost (video-sku-cost pricing options family price)]
                                 {:family family
                                  :unit (-> family
                                            (str/replace #"^text_to_video_" "")
                                            (str/replace #"_with(?:out)?_audio$" ""))
                                  :audio? (str/ends-with? family "_with_audio")
                                  :qualified? (some? qualifier)
                                  :cost cost}))))
                         (:pricing_skus pricing))
        qualified (filter :qualified? candidates)
        replaced? (fn [{:keys [family unit audio?]}]
                    (some #(and (= unit (:unit %))
                                (or (= family (:family %))
                                    (not audio?)))
                          qualified))
        costs (map :cost (concat qualified
                                 (remove #(or (:qualified? %) (replaced? %))
                                         candidates)))
        minimum (some-> (get-in pricing [:pricing_skus :minimum_cents_per_generation])
                         number-value (/ 100.0))]
    (when (seq costs)
      (max (apply max costs) (or minimum 0.0)))))

(defn video-price
  "Estimated customer USD, including the single combined markup."
  [model options]
  (let [options (video-options model options)
        pricing (:model/video-pricing model)
        cost (video-cost-estimate pricing options)]
    (when (and cost (:markup pricing))
      (* cost (:markup pricing)))))

(defn format-usd
  "Rounds half up with the same float arithmetic on the JVM and in the
   browser before formatting: `String/format` and `toFixed` alone render
   1.105 as $1.11 and $1.10, so catalog and modal labels disagreed."
  [amount]
  (let [digits (if (< amount 0.01) 3 2)
        scale (if (= 3 digits) 1000 100)
        rounded (/ (double (#?(:clj Math/round :cljs js/Math.round)
                            (double (* amount scale))))
                   scale)]
    (str "$" #?(:clj (String/format java.util.Locale/US
                                    (str "%." digits "f")
                                    (to-array [rounded]))
                :cljs (.toFixed rounded digits)))))

(defn modal-price-label
  "Recomputed from the current form data on every render. Ratios that do
   not affect a provider's per-second rate correctly leave its price alone."
  [model category options]
  (if (= :video category)
    (let [options (video-options model options)
          price (video-price model options)
          hints (option-hints options)]
      (str (if price (str "~" (format-usd price)) "Pricing unavailable")
           (when (seq hints) (str " / " hints))))
    (:model/price-label model)))
